import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import _ from 'lodash'
import classnames from 'classnames'

import { getCustomIntl } from 'config/config'
import Grid from '../../atoms/FoundationGrid/Grid'
import Cell from '../../atoms/FoundationGrid/Cell'
import GridContainer from '../../atoms/FoundationGrid/GridContainer'
import InputDropDown from '../../atoms/InputDropDown'
import CompareCard from '../../atoms/CompareCard'
import CustomHighchart from '../../molecules/CustomHighchart'
import Table from '../../molecules/Table'
import Tooltip from '../../atoms/Tooltip'
import MorningStar from '../../atoms/MorningStar'
import PageLevelMessage from '../../atoms/PageLevelMessage'
import Loader from '../../atoms/Loader'

import './style.scss'

const FundsCompare = ({
  comparisonData,
  comparisonCardData,
  addComparisonCardData,
  cancelComparisonCardData,
  performanceChartConfig,
  handleInput,
  dropdownFundsList,
  comparisonLimit,
  selectedCount,
  isPerformanceTableError,
  isChartError,
  isFundAdding,
  isMobileSize
}) => {
  const [cardTransformation, setCardTransformation] = useState(0)
  const [isSticky, setStickyStatus] = useState(false)

  const isStickyRef = useRef(isSticky)

  const setStickyStatusRef = (x) => {
    isStickyRef.current = x
    setStickyStatus(x)
  }

  const handleSelect = (selectedOption) => {
    addComparisonCardData(selectedOption)
  }

  const handleCancel = (selectedOption) => {
    cancelComparisonCardData(selectedOption)
  }

  const intl = getCustomIntl()

  const { rateOfReturnData, detailData, metricsData, ratingData } = comparisonData

  const necessaryElements = {
    MorningStar
  }

  const handleScroll = (e) => {
    setCardTransformation(e.target.scrollLeft)
  }

  const ref = useRef()

  const [chartFlag, setChartFlag] = useState(false)
  const [savedPerformanceChartConfig, savePerformanceChartConfig] = useState()

  useEffect(() => {
    window.onscroll = () => {
      if (ref.current) {
        if (parseInt(ref.current.getBoundingClientRect().top, 10) <= 0) {
          if (!isStickyRef.current) {
            setStickyStatusRef(true)
          }
        } else if (isStickyRef.current) {
          setStickyStatusRef(false)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (!_.isEqual(performanceChartConfig, savedPerformanceChartConfig)) {
      savePerformanceChartConfig(performanceChartConfig)
      setChartFlag(!chartFlag)
    }
  }, [performanceChartConfig])

  return (
    <>
      {isFundAdding && (
        <div className="full-screen">
          <Loader />
        </div>
      )}
      <GridContainer extraClasses="fil-fund-compare">
        <Grid direction="grid-x" extraClasses="fil-fund-compare__search sticky-row">
          <Cell>
            <label id="fil-fund-compare__search-label" className="text-heading-s fil-fund-compare__search-label">
              <FormattedMessage id="fundComparison.fundComparePage.fundCompare" />
            </label>
          </Cell>
          <Cell extraClasses={classnames('text-left', comparisonCardData.length > 0 ? '' : 'landing-page')}>
            <Grid>
              <Cell extraClasses={classnames('large-8', comparisonCardData.length > 0 ? '' : 'large-offset-2')}>
                <label className="landing-page__label">
                  <FormattedMessage id="fundComparison.fundComparePage.header" />
                </label>
                <InputDropDown
                  fundsList={dropdownFundsList}
                  // inputValue={inputDropDownState.value}
                  handleInput={handleInput}
                  handleSelect={handleSelect}
                  selectedSuffix={intl.formatMessage({ id: 'fundComparison.fundComparePage.selected' })}
                  visitedLabel={intl.formatMessage({ id: 'fundComparison.fundComparePage.visitedFunds' })}
                  inputProps={{
                    id: 'compareSearchInput',
                    placeholder: intl.formatMessage({ id: 'fundList.filter.search.placeholder' }),
                    icon: 'search'
                  }}
                  noDataPlaceholder={intl.formatMessage({ id: 'fundComparison.fundComparePage.noSearchData' })}
                  maxSelected={comparisonLimit}
                  selectedCount={selectedCount}
                  size={isMobileSize ? 'small' : ''}
                  {...(comparisonCardData.length > 0 ? {} : { inversed: 'inversed' })}
                />
                <div className="landing-page__tips">
                  <FormattedMessage id="fundComparison.fundComparePage.compareTip" />
                </div>
              </Cell>
            </Grid>
          </Cell>
        </Grid>
        <div className="sticky-container" onScroll={handleScroll}>
          <React.Fragment>
            {comparisonCardData.length > 0 && (
              <div
                className={classnames('fil-fund-compare__card-container', {
                  'is-pinned': isStickyRef.current
                })}
                ref={ref}
              >
                <div
                  className={classnames('grid-x pinned-row', {
                    'is-pinned': isStickyRef.current
                  })}
                >
                  <div className="fil-fund-compare__card">
                    <Cell extraClasses="fil-fund-compare__card-header">
                      <label className="fil-fund-compare__card-header__label">
                        <FormattedMessage id="fundComparison.fundComparePage.cardLabel" />
                      </label>
                      <label className="fil-fund-compare__card-header__value">{comparisonCardData.length}/5</label>
                    </Cell>
                    <div
                      className="fil-fund-compare__card-container"
                      style={isStickyRef.current ? { transform: `translateX(-${cardTransformation}px)` } : {}}
                    >
                      {comparisonCardData.map((item, index) => (
                        <Cell extraClasses="fil-fund-compare__card-item" key={item.id}>
                          <CompareCard
                            key={`fil-fund-compare__card-item-${item.id}`}
                            fundItem={item}
                            index={index}
                            handleCancel={handleCancel}
                          />
                        </Cell>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>

          <React.Fragment>
            {!_.isEmpty(comparisonData) && (
              <React.Fragment>
                <Grid direction="grid-x" extraClasses="align-justify fil-fund-compare__chart sticky-row">
                  <Cell extraClasses="text-left fil-fund-compare__chart-title">
                    <span className="text-heading-s fil-fund-compare__chart-title-content">
                      <FormattedMessage id="fundComparison.fundComparePage.fundPerformance" />
                    </span>
                  </Cell>
                  {!isChartError ? (
                    <React.Fragment>
                      {!_.isEmpty(performanceChartConfig) ? (
                        <Cell extraClasses="text-left large-12">
                          {chartFlag && (
                            <CustomHighchart id="fund-comparison-chart" hasDatePicker={false} options={performanceChartConfig} />
                          )}
                          {!chartFlag && (
                            <CustomHighchart id="fund-comparison-chart" hasDatePicker={false} options={performanceChartConfig} />
                          )}
                        </Cell>
                      ) : (
                        <Cell extraClasses="text-left large-12">
                          <Loader />
                        </Cell>
                      )}
                    </React.Fragment>
                  ) : (
                    <Cell extraClasses="small-12 large-8">
                      <PageLevelMessage
                        id="fil-fund-comparison__performance-chart-error-msg"
                        content={intl.formatMessage({ id: 'fundComparison.fundComparePage.dataErrorMessage' })}
                      />
                    </Cell>
                  )}
                </Grid>
                <Grid extraClasses="fil-fund-compare__header sticky-row">
                  <Cell extraClasses="text-left">
                    <span className="text-heading-s">
                      <FormattedMessage id="fundComparison.fundComparePage.tableHeaderRateOfReturn" />
                    </span>
                  </Cell>
                </Grid>
                {!isPerformanceTableError && (
                  <Grid direction="grid-x" extraClasses="align-justify fil-fund-compare__table">
                    <Cell extraClasses="large-12 xlarge-10">
                      <Table
                        tableId="fil-fund-compare__table-rate-of-return"
                        extraClasses="fil-fund-compare__table-component"
                        textSize="small"
                        {...{
                          needInitSort: false,
                          hasMobileSort: false,
                          isSimpleTable: true,
                          tableData: rateOfReturnData.tableData
                        }}
                      />
                    </Cell>
                  </Grid>
                )}
                {isPerformanceTableError && (
                  <Cell extraClasses="small-12 large-8">
                    <PageLevelMessage
                      id="fil-fund-comparison__performance-table-error-msg"
                      content={intl.formatMessage({ id: 'fundComparison.fundComparePage.dataErrorMessage' })}
                    />
                  </Cell>
                )}
                <Grid extraClasses="fil-fund-compare__header sticky-row">
                  <Cell extraClasses="text-left">
                    <span className="text-heading-s">
                      <FormattedMessage id="fundComparison.fundComparePage.tableHeaderDetail" />
                    </span>
                  </Cell>
                </Grid>
                <Grid direction="grid-x" extraClasses="align-justify fil-fund-compare__table">
                  <Cell extraClasses="large-12 xlarge-10">
                    <Table
                      tableId="fil-fund-compare__table-detail"
                      extraClasses="fil-fund-compare__table-component"
                      textSize="small"
                      {...{
                        needInitSort: false,
                        hasMobileSort: false,
                        isSimpleTable: true,
                        tableData: detailData.tableData
                      }}
                    />
                  </Cell>
                </Grid>
                <Grid extraClasses="fil-fund-compare__header sticky-row">
                  <Cell extraClasses="text-left">
                    <span className="text-heading-s fil-fund-compare__header-tooltip">
                      <FormattedMessage id="fundComparison.fundComparePage.tableHeaderMetrics" />
                    </span>
                    <span className="fil-fund-compare__header-tooltip__component">
                      <Tooltip
                        fontSize="18"
                        alignmen="left"
                        message={intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.toolTipContent' })}
                      />
                    </span>
                  </Cell>
                </Grid>
                <Grid direction="grid-x" extraClasses="align-justify fil-fund-compare__table">
                  <Cell extraClasses="large-12 xlarge-10">
                    <Table
                      tableId="fil-fund-compare__table-metrics"
                      extraClasses="fil-fund-compare__table-component"
                      textSize="small"
                      {...{
                        needInitSort: false,
                        hasMobileSort: false,
                        isSimpleTable: true,
                        tableData: metricsData.tableData
                      }}
                    />
                  </Cell>
                </Grid>
                <Grid extraClasses="fil-fund-compare__header sticky-row">
                  <Cell extraClasses="text-left">
                    <span className="text-heading-s">
                      <FormattedMessage id="fundComparison.fundComparePage.tableHeaderRating" />
                    </span>
                  </Cell>
                </Grid>
                <Grid direction="grid-x" extraClasses="align-justify fil-fund-compare__table">
                  <Cell extraClasses="large-12 xlarge-10">
                    <Table
                      tableId="fil-fund-compare__table-rating"
                      extraClasses="fil-fund-compare__table-component"
                      textSize="small"
                      {...{
                        needInitSort: false,
                        hasMobileSort: false,
                        isSimpleTable: true,
                        tableData: ratingData.tableData
                      }}
                      necessaryElements={necessaryElements}
                    />
                  </Cell>
                </Grid>
              </React.Fragment>
            )}
          </React.Fragment>
        </div>
      </GridContainer>
    </>
  )
}

FundsCompare.propTypes = {
  performanceChartConfig: PropTypes.object,
  comparisonData: PropTypes.object,
  comparisonCardData: PropTypes.array,
  addComparisonCardData: PropTypes.func.isRequired,
  cancelComparisonCardData: PropTypes.func.isRequired,
  handleInput: PropTypes.func,
  dropdownFundsList: PropTypes.array,
  comparisonLimit: PropTypes.number,
  selectedCount: PropTypes.number,
  isPerformanceTableError: PropTypes.bool.isRequired,
  isChartError: PropTypes.bool.isRequired,
  isFundAdding: PropTypes.bool.isRequired,
  isMobileSize: PropTypes.bool.isRequired
}

FundsCompare.defaultProps = {
  performanceChartConfig: {},
  handleInput: () => {},
  dropdownFundsList: [],
  comparisonData: {},
  comparisonCardData: [],
  comparisonLimit: 5,
  selectedCount: 0
}

export default FundsCompare
